/**
 * MentionAwareComposer
 *
 * Lexical-based composer with three inline mention pickers:
 *   - `#` opens a list of **surfaces** in the active conversation.
 *   - `@` opens a list of **agents** the user can reach (cross-space).
 *   - `!` opens a list of **files** in the active conversation's VFS.
 *         Chip displays just the filename; the full path is sent to the
 *         agent via `fileRefs` and shown on hover via `title`.
 *
 * Built on `@assistant-ui/react-lexical`'s `LexicalComposerInput` plus the
 * trigger-popover primitives in `@assistant-ui/react@^0.12.27`. Multiple
 * `Unstable_TriggerPopover`s can sit under one `Unstable_TriggerPopoverRoot`,
 * each bound to its own `char` + adapter.
 *
 * Selection flow (same for both pickers):
 *   1. User types `#` or `@`. The Lexical-side `DirectivePlugin` detects the
 *      trigger and the matching popover opens.
 *   2. The list rendered below comes from `unstable_useMentionAdapter`,
 *      seeded with either surfaces (`useCanvases()`) or agents
 *      (`useMeAgents()`). The adapter's built-in fuzzy match handles
 *      type-to-filter.
 *   3. Picking a row (Enter / Tab / mouse click) inserts an inline
 *      `DirectiveNode` chip and Lexical serializes it into `composer.text`
 *      as `:surface[label]{name=cvs-…:srf-…}` or `:agent[label]{name=agt-…}`.
 *   4. On submit, `useAsyncInvokeAdapter` parses that text via
 *      `extractMentions(...)` to recover `surfaceRefs` + `agentRefs` and the
 *      clean message.
 *
 * Known dev-only quirk: under React `StrictMode`, mouse-clicking a popover
 * row throws `"Version is less than committed version"` from
 * `@assistant-ui/tap`'s reactive resource system (its rollback-on-effect
 * logic mis-detects the second pass as a stale render). Production builds
 * disable StrictMode; keyboard selection (Enter / Tab / Arrow keys) works
 * in both. Tracking upstream — remove this note when fixed.
 *
 * Same-canvas constraint: once the user inserts the first surface mention,
 * we derive `activeCanvasId` from the composer text and filter the surface
 * candidate pool to that canvas, so the picker simply hides cross-canvas
 * surfaces. Agent mentions aren't constrained.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useHotkeys } from 'react-hotkeys-hook';
import {
  ComposerPrimitive,
  ThreadPrimitive,
  unstable_useMentionAdapter,
  type Unstable_Mention,
} from '@assistant-ui/react';
import { LexicalComposerInput } from '@assistant-ui/react-lexical';
import { useQueryClient } from '@tanstack/react-query';
import { Bot, CircleStop, Mic, Paperclip, Plus, RefreshCw, SendHorizontal, Volume2, VolumeX } from 'lucide-react';
import { cn } from '@/lib/cn';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { shouldAutoFocusInputs } from '../../utils/constrained-device';
import { unlockAudioPlayback } from '../../utils/audio-theme';
import { useCanvases } from '../../contexts/CanvasContext';
import { useMeAgent, useMeAgents } from '../../hooks/useAgents';
import { useMentionFileCandidates } from '../../hooks/useMentionFileCandidates';
import { useVoiceAvailability } from '../../voice/use-voice-availability';
import { useVoiceSession, voiceSessionStore } from '../../voice/voice-session-store';
import { readAloudStore, useReadAloudEnabled } from '../../voice/read-aloud';
import { VoiceSessionBar } from './voice-session-bar';
import {
  MENTION_TYPE_AGENT,
  MENTION_TYPE_SURFACE,
  buildSurfaceRefId,
} from '../../utils/directive-mentions';
import { useComposerAttachments } from './composer-attachments-context';
import { ComposerAttachmentChip } from './composer-attachment-chip';
import { cleanDisplayText } from '@/utils/display-text';

export function MentionAwareComposer({ agentId }: { agentId?: string } = {}) {
  const { canvases, surfaces, loadCanvasSurfaces, conversationId } = useCanvases();
  const { data: agentsList } = useMeAgents();
  // The `!` picker merges conversation VFS files with files from the KBs the
  // agent can reach. KB walks are expensive, so they stay dormant until the
  // user first types `!` (see `fileMentionsEnabled`). Until then only the
  // conversation files load.
  const [fileMentionsEnabled, setFileMentionsEnabled] = useState(false);
  const fileItems = useMentionFileCandidates({
    conversationId,
    agentId,
    enabled: fileMentionsEnabled,
  });

  // The CanvasProvider lazy-fetches surfaces only when the user clicks a
  // canvas tab. Without this, pressing `#` would only show surfaces from
  // canvases the user has already opened. Prefetch every canvas so the
  // popover always reflects the full conversation. `loadCanvasSurfaces`
  // dedupes against already-loaded/loading ids, so re-runs from new
  // canvases arriving via WebSocket cost nothing extra.
  useEffect(() => {
    for (const cv of canvases.values()) {
      void loadCanvasSurfaces(cv.canvasId);
    }
  }, [canvases, loadCanvasSurfaces]);

  // Stable item list — every surface in the active conversation is
  // available. Cross-canvas combinations are validated at dispatch by
  // the backend (and surfaced as a clear error there). Filtering reactively
  // on `composer.text` here triggers an internal version-tracking
  // invariant in `@assistant-ui/tap`'s reactive resource system at
  // selection time.
  const surfaceItems = useMemo<Unstable_Mention[]>(() => {
    const out: Unstable_Mention[] = [];
    for (const surface of surfaces.values()) {
      const cv = canvases.get(surface.canvasId);
      out.push({
        id: buildSurfaceRefId(surface.canvasId, surface.surfaceId),
        type: MENTION_TYPE_SURFACE,
        label: cleanDisplayText(surface.title) || surface.surfaceId,
        metadata: {
          canvasId: surface.canvasId,
          surfaceId: surface.surfaceId,
          canvasTitle: cleanDisplayText(cv?.title) || 'Canvas',
        },
      });
    }
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [surfaces, canvases]);

  // Agents the user can reach across spaces. No subAgentIds filtering — the
  // backend decides whether a mention can actually be routed; here we just
  // expose the full reachable set.
  const agentItems = useMemo<Unstable_Mention[]>(() => {
    const items = agentsList?.items ?? [];
    const out: Unstable_Mention[] = items.map((agent) => ({
      id: agent.agentId,
      type: MENTION_TYPE_AGENT,
      label: agent.name,
      metadata: { description: agent.description ?? '' },
    }));
    out.sort((a, b) => a.label.localeCompare(b.label));
    return out;
  }, [agentsList]);

  const surfaceMention = unstable_useMentionAdapter({
    items: surfaceItems,
    includeModelContextTools: false,
  });
  const agentMention = unstable_useMentionAdapter({
    items: agentItems,
    includeModelContextTools: false,
  });
  const fileMention = unstable_useMentionAdapter({
    items: fileItems,
    includeModelContextTools: false,
  });

  // After the directive plugin inserts a chip (cursor is positioned right
  // after it), drop a trailing space so the user can keep typing without
  // pressing space first. We dispatch through `document.execCommand` so
  // Lexical's `beforeinput` listener picks it up and creates a real text
  // node — bypassing this and writing to the DOM directly would leave
  // Lexical's editor state out of sync.
  const handleDirectiveSelect = useCallback(() => {
    requestAnimationFrame(() => {
      document.execCommand('insertText', false, ' ');
    });
  }, []);

  // Lazily wake the KB file walks the first time the user types `!` — that's
  // when the file popover opens, so we pay the KB-listing cost only on demand
  // (and never on a composer that's only used for plain prose). Stays enabled
  // afterwards so re-opening the picker is warm.
  const handleComposerKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLFormElement>) => {
      if (e.key === '!') setFileMentionsEnabled(true);
    },
    [],
  );

  // Returning to the tab (OAuth child tab closed, app switch) leaves focus on
  // the document body — the composer caret stops blinking and typing goes
  // nowhere until a click. Re-focus the input on window focus / tab return,
  // but only when nothing else holds focus (never steal from another input)
  // and never on touch devices (popping the on-screen keyboard uninvited —
  // same gate as the mount-time autoFocus below).
  useEffect(() => {
    const refocus = () => {
      if (document.hidden || !shouldAutoFocusInputs()) return;
      const active = document.activeElement;
      if (active && active !== document.body) return;
      document.querySelector<HTMLElement>('.aui-lexical-input')?.focus();
    };
    window.addEventListener('focus', refocus);
    document.addEventListener('visibilitychange', refocus);
    return () => {
      window.removeEventListener('focus', refocus);
      document.removeEventListener('visibilitychange', refocus);
    };
  }, []);

  // Esc focuses the composer when pressed outside any input. `react-hotkeys-hook`
  // ignores key events fired in inputs/textareas/contentEditable by default, so
  // typing in the composer (or another input) doesn't trigger this — Esc still
  // bubbles to the Lexical `cancelOnEscape` handler / popover dismiss logic.
  // From any non-input context (canvas surfaces, scrolled thread, sidebar),
  // Esc lifts focus straight back to the composer.
  useHotkeys(
    'escape',
    () => {
      const el = document.querySelector<HTMLElement>('.aui-lexical-input');
      el?.focus();
    },
    { description: 'Focus the message composer', metadata: { group: 'Composer' } },
  );

  // While a voice session is anything but idle, the input row is replaced by
  // the session bar. Replacement is via CSS (`hidden`), not unmount — tearing
  // down the Lexical editor would drop any draft text and re-trip the
  // `@assistant-ui/tap` remount quirks this file already documents.
  const voiceSession = useVoiceSession();
  const voiceActive = voiceSession.status !== 'idle';

  return (
    // `relative` so the absolutely-positioned trigger popover anchors to the form.
    // `flex-nowrap` keeps the send button anchored at the bottom-right when the
    // input grows multi-line — without it, the default `.aui-composer-root`
    // `flex-wrap` lets the button drop to a new row. `min-h-16` (64px) sets the
    // empty-state pill height — enough for one `leading-7` line (28px) plus
    // the `!py-4` (32px) wrapper padding plus border — and lets the root grow
    // as the Lexical input adds rows (capped by `.aui-lexical-input`'s
    // `max-h-64`, after which it scrolls). `items-end` keeps the send button
    // hugging the bottom-right when the text grows multi-line. `rounded-3xl`
    // gives the pill-shaped corners we want.
    <ComposerPrimitive.Root
      onKeyDownCapture={handleComposerKeyDown}
      className="aui-composer-root relative flex-col flex-nowrap items-stretch rounded-3xl border border-border bg-muted px-2 shadow-2xl shadow-black/40"
    >
      <ComposerAttachmentsRow />
      {voiceActive && <VoiceSessionBar />}
      <div className={cn('flex min-h-16 flex-nowrap items-end', voiceActive && 'hidden')}>
        <ComposerPrimitive.Unstable_TriggerPopoverRoot>
          <ComposerPlusAction />
          <LexicalComposerInput
          // Only steal focus on fine-pointer (desktop/laptop) devices — on
          // tablets / car screens (Tesla) focusing pops the on-screen
          // keyboard over half the viewport before the user asked to type.
          autoFocus={shouldAutoFocusInputs()}
          placeholder="How can I help you today?"
          directivePluginProps={{ onDirectiveSelect: handleDirectiveSelect }}
          // The default `aui-composer-input` styling targets a `<textarea>`;
          // Lexical renders a wrapper `<div>` around a contentEditable + a
          // sibling placeholder `<div>`. We re-create the textarea look:
          //  - the wrapper is `relative` so the placeholder can overlay
          //  - the contentEditable gets the padding / outline / max-height
          //  - the placeholder sits inside the same padding box, dimmed
          className={cn(
            'aui-composer-input',
            'relative flex-grow self-stretch',
            // Upstream `.aui-lexical-editor` (the wrapper this className lands
            // on) sets its own `overflow-y: auto` and a hard `max-height: 10rem`
            // (160px). The hard cap clips the composer well before the inner
            // contenteditable's `max-h-64` (256px) is reached — override with
            // `!max-h-none` so the wrapper grows with its child, and hide the
            // scrollbar chrome for the same reason as the inner: composer
            // scroll should be wheel/keyboard only, no visible track.
            '!max-h-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            // The `.aui-composer-input` rule from `@assistant-ui/react-ui`
            // CSS sets `padding: 1rem 0.5rem` and `font-size: 0.875rem` at the
            // same specificity as a Tailwind utility — and its stylesheet is
            // imported AFTER ours in some bundles, so plain `py-4` lost the
            // cascade. `!`-prefixed utilities emit `!important`, which wins
            // unconditionally. The asymmetric `pt-[17px] pb-[15px]` (vs a
            // straight py-4) nudges the text one pixel down so its centreline
            // matches the `+` and send buttons; total height is unchanged.
            // The placeholder's `top-[17px]` mirrors the new pt so the empty
            // state lines up with where the user starts typing.
            '!pt-[17px] !pb-[15px]',
            // Cap the inner contenteditable so a long paste can't push the
            // conversation off-screen, but hide the scrollbar chrome — users
            // can still wheel/keyboard-scroll, matching ChatGPT/Claude.ai's
            // composer. `max-h-64` (16rem ≈ 9 lines at leading-7) lets the
            // composer grow more before the cap kicks in. The arbitrary
            // `[&_.aui-lexical-input::-webkit-scrollbar]` variant targets the
            // WebKit scrollbar pseudo-element on the inner contenteditable;
            // `[scrollbar-width:none]` does the same for Firefox.
            '[&_.aui-lexical-input]:max-h-64 [&_.aui-lexical-input]:overflow-y-auto',
            '[&_.aui-lexical-input]:[scrollbar-width:none] [&_.aui-lexical-input::-webkit-scrollbar]:hidden',
            '[&_.aui-lexical-input]:!text-base [&_.aui-lexical-input]:!leading-7 [&_.aui-lexical-input]:outline-none',
            '[&_.aui-lexical-placeholder]:pointer-events-none [&_.aui-lexical-placeholder]:absolute [&_.aui-lexical-placeholder]:left-2 [&_.aui-lexical-placeholder]:top-[17px] [&_.aui-lexical-placeholder]:!text-base [&_.aui-lexical-placeholder]:!leading-7 [&_.aui-lexical-placeholder]:text-muted-foreground',
            // Lexical inserts mentions as `data-lexical-decorator` spans
            // wrapping `.aui-directive-chip`. Style them as inline pill
            // tokens so they look like chips, not plain text. The shape +
            // layout is shared; the color split distinguishes mention type:
            // surfaces use neutral muted, agents use the subagent teal
            // token so the operator sees at a glance which is which.
            '[&_.aui-directive-chip]:inline-flex [&_.aui-directive-chip]:items-center [&_.aui-directive-chip]:gap-1 [&_.aui-directive-chip]:rounded-[14px] [&_.aui-directive-chip]:px-1.5 [&_.aui-directive-chip]:py-px',
            '[&_.aui-directive-chip]:cursor-pointer [&_.aui-directive-chip]:select-none',
            // Note: the composer wrapper is `bg-muted`, so the surface chip
            // can't use `bg-muted` (it would disappear). `bg-foreground/10`
            // matches the rendered bubble chip in `MentionAwareUserText`.
            '[&_.aui-directive-chip[data-directive-type="surface"]]:bg-foreground/10 [&_.aui-directive-chip[data-directive-type="surface"]]:text-foreground',
            '[&_.aui-directive-chip[data-directive-type="agent"]]:bg-accent-subagent-fg/15 [&_.aui-directive-chip[data-directive-type="agent"]]:text-accent-subagent-fg',
            // File chip: same understated tone as surfaces (the agent
            // chip's teal is reserved for delegation), distinguished by
            // the paperclip icon rendered in the message bubble.
            '[&_.aui-directive-chip[data-directive-type="file"]]:bg-foreground/10 [&_.aui-directive-chip[data-directive-type="file"]]:text-foreground',
          )}
        />
        <ComposerReadAloudAction agentId={agentId} conversationId={conversationId} />
        <ComposerVoiceAction agentId={agentId} conversationId={conversationId} />
        <ComposerAction />
        <ComposerPrimitive.Unstable_TriggerPopover
          char="#"
          adapter={surfaceMention.adapter}
          className="absolute bottom-full left-0 right-0 z-50 mb-2 max-h-64 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-md"
        >
          <ComposerPrimitive.Unstable_TriggerPopover.Directive {...surfaceMention.directive} />
          <ComposerPrimitive.Unstable_TriggerPopoverItems>
            {(rendered) =>
              rendered.length === 0 ? (
                <div className="px-3 py-1.5 text-xs text-muted-foreground">
                  No surfaces in this conversation yet.
                </div>
              ) : (
                rendered.map((item, idx) => (
                  <ComposerPrimitive.Unstable_TriggerPopoverItem
                    key={item.id}
                    item={item}
                    index={idx}
                    // Keep focus on the Lexical editor — without this, the
                    // mousedown on the button takes focus first and Lexical
                    // commits its current editor state mid-cycle, tripping
                    // an internal version-tracking invariant in
                    // `@assistant-ui/tap`'s reactive resource system.
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs transition-colors',
                      'data-[highlighted]:bg-muted data-[highlighted]:text-foreground',
                      'hover:bg-muted/60',
                    )}
                  >
                    <RefreshCw className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="font-medium">{item.label}</span>
                  </ComposerPrimitive.Unstable_TriggerPopoverItem>
                ))
              )
            }
          </ComposerPrimitive.Unstable_TriggerPopoverItems>
        </ComposerPrimitive.Unstable_TriggerPopover>
        <ComposerPrimitive.Unstable_TriggerPopover
          char="@"
          adapter={agentMention.adapter}
          className="absolute bottom-full left-0 right-0 z-50 mb-2 max-h-64 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-md"
        >
          <ComposerPrimitive.Unstable_TriggerPopover.Directive {...agentMention.directive} />
          <ComposerPrimitive.Unstable_TriggerPopoverItems>
            {(rendered) =>
              rendered.length === 0 ? (
                <div className="px-3 py-1.5 text-xs text-muted-foreground">
                  No agents available.
                </div>
              ) : (
                rendered.map((item, idx) => (
                  <ComposerPrimitive.Unstable_TriggerPopoverItem
                    key={item.id}
                    item={item}
                    index={idx}
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs transition-colors',
                      'data-[highlighted]:bg-muted data-[highlighted]:text-foreground',
                      'hover:bg-muted/60',
                    )}
                  >
                    <Bot className="h-3 w-3 shrink-0 text-accent-subagent-fg" />
                    <span className="font-medium">{item.label}</span>
                  </ComposerPrimitive.Unstable_TriggerPopoverItem>
                ))
              )
            }
          </ComposerPrimitive.Unstable_TriggerPopoverItems>
        </ComposerPrimitive.Unstable_TriggerPopover>
        <ComposerPrimitive.Unstable_TriggerPopover
          char="!"
          adapter={fileMention.adapter}
          className="absolute bottom-full left-0 right-0 z-50 mb-2 max-h-64 overflow-y-auto rounded-xl border border-border bg-popover p-1 shadow-md"
        >
          <ComposerPrimitive.Unstable_TriggerPopover.Directive {...fileMention.directive} />
          <ComposerPrimitive.Unstable_TriggerPopoverItems>
            {(rendered) =>
              rendered.length === 0 ? (
                <div className="px-3 py-1.5 text-xs text-muted-foreground">
                  No files available.
                </div>
              ) : (
                rendered.map((item, idx) => (
                  <ComposerPrimitive.Unstable_TriggerPopoverItem
                    key={item.id}
                    item={item}
                    index={idx}
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(
                      'flex w-full cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-left text-xs transition-colors',
                      'data-[highlighted]:bg-muted data-[highlighted]:text-foreground',
                      'hover:bg-muted/60',
                    )}
                  >
                    <Paperclip className="h-3 w-3 shrink-0 text-muted-foreground" />
                    <span className="min-w-0 flex-1 truncate font-mono">
                      {(item.metadata?.description as string | undefined) ?? item.label}
                    </span>
                    {/* Source tag — which store the file lives in (the KB name,
                        or "Conversation"). Pinned right; pushes nothing when
                        the path truncates thanks to the `flex-1 min-w-0` above. */}
                    <span className="shrink-0 rounded-md bg-foreground/10 px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
                      {(item.metadata?.sourceLabel as string | undefined) ?? 'Conversation'}
                    </span>
                  </ComposerPrimitive.Unstable_TriggerPopoverItem>
                ))
              )
            }
          </ComposerPrimitive.Unstable_TriggerPopoverItems>
        </ComposerPrimitive.Unstable_TriggerPopover>
        </ComposerPrimitive.Unstable_TriggerPopoverRoot>
      </div>
    </ComposerPrimitive.Root>
  );
}

/** Row of attachment chips above the input. Renders nothing when empty. */
function ComposerAttachmentsRow() {
  const attachmentsCtx = useComposerAttachments();
  if (!attachmentsCtx || attachmentsCtx.attachments.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2 px-1 pt-2 pb-1">
      {attachmentsCtx.attachments.map((job) => (
        <ComposerAttachmentChip
          key={job.id}
          job={job}
          onRemove={() => attachmentsCtx.remove(job.id)}
        />
      ))}
    </div>
  );
}

/** `+` button to the left of the input. Opens a hidden file picker. */
function ComposerPlusAction() {
  const attachmentsCtx = useComposerAttachments();
  const inputRef = useRef<HTMLInputElement>(null);
  if (!attachmentsCtx) return null;
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = Array.from(e.target.files ?? []);
          if (files.length) attachmentsCtx.addFiles(files);
          e.target.value = '';
        }}
      />
      <button
        type="button"
        aria-label="Attach files"
        title="Attach files"
        onClick={() => inputRef.current?.click()}
        className={cn(
          // The send button uses `mb-[14px]` to compensate for the paper-plane
          // glyph's top-weight — the `+` is symmetric and doesn't need that
          // bias, so `mb-[15px]` puts its centre on the text centreline.
          'mb-[15px] size-9 shrink-0 rounded-full',
          'inline-flex items-center justify-center',
          'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
        )}
      >
        <Plus className="h-5 w-5" />
      </button>
    </>
  );
}

/**
 * Spoken-replies button. Two engines behind one affordance:
 *
 * - Voice-capable agent (enableVoice + conversation + credential not
 *   definitively missing): clicking starts a MIC-LESS realtime voice session
 *   — no permission prompt, typed turns in (`voice_text`), real Grok speech
 *   out. The composer swaps to the session bar with its typed input.
 * - Everywhere else: toggles browser-TTS read-aloud of assistant answers
 *   (see `ReadAloudOnComplete`) — clearly labelled as the browser voice so
 *   nobody mistakes it for Grok.
 */
function ComposerReadAloudAction({
  agentId,
  conversationId,
}: {
  agentId?: string;
  conversationId?: string;
}) {
  const enabled = useReadAloudEnabled();
  const { data: agent } = useMeAgent(agentId);
  const availability = useVoiceAvailability(agent?.spaceId);
  const queryClient = useQueryClient();

  const grokCapable =
    !!agent?.enableVoice && !!conversationId && availability !== 'unavailable';

  if (grokCapable) {
    const startTypedSession = () => {
      // MUST run synchronously inside the click: Safari only honors
      // AudioContext.resume() from a gesture handler — unlock before the
      // session's async handshake or replies play into a suspended graph.
      unlockAudioPlayback();
      void voiceSessionStore.start({
        conversationId: conversationId!,
        agentId: agent.agentId,
        spaceId: agent.spaceId,
        withMic: false,
        onSessionEnded: ({ conversationId: cid }) => {
          void queryClient.invalidateQueries({
            predicate: (q) => q.queryKey.includes(cid),
          });
        },
      });
    };
    return (
      <ThreadPrimitive.If running={false}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              type="button"
              aria-label="Voice replies — type, the agent talks"
              onClick={startTypedSession}
              className={cn(
                'mb-[15px] size-9 shrink-0 rounded-full',
                'inline-flex items-center justify-center',
                'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
              )}
            >
              <Volume2 className="h-5 w-5" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top">
            Voice replies (Grok) — type, the agent talks back
          </TooltipContent>
        </Tooltip>
      </ThreadPrimitive.If>
    );
  }

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={enabled ? 'Turn off read aloud' : 'Read responses aloud'}
          aria-pressed={enabled}
          onClick={() => readAloudStore.setEnabled(!enabled)}
          className={cn(
            'mb-[15px] size-9 shrink-0 rounded-full',
            'inline-flex items-center justify-center',
            enabled
              ? 'bg-primary/15 text-primary hover:bg-primary/25'
              : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
          )}
        >
          {enabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
        </button>
      </TooltipTrigger>
      <TooltipContent side="top">
        {enabled
          ? 'Read aloud is on — responses use the browser voice'
          : 'Read responses aloud (browser voice)'}
      </TooltipContent>
    </Tooltip>
  );
}

/**
 * Mic button — starts a realtime voice session. Rendered only when the agent
 * has `enableVoice` and the conversation already exists (a fresh chat gets a
 * conversationId on its first text turn; voice-from-scratch is a follow-up).
 *
 * Availability gating (`useVoiceAvailability`): `available`/`unknown` render
 * normally — `unknown` means a non-admin can't see the org credential tier,
 * so we stay optimistic and let the server's `VOICE_CREDENTIAL_MISSING`
 * error frame be the authority. A definitive `unavailable` (admin, no xAI
 * key anywhere) renders muted with a tooltip, and the click shows the
 * credential error panel WITHOUT prompting for mic permission — never ask
 * for the mic when the session can't start.
 */
function ComposerVoiceAction({
  agentId,
  conversationId,
}: {
  agentId?: string;
  conversationId?: string;
}) {
  const { data: agent } = useMeAgent(agentId);
  const availability = useVoiceAvailability(agent?.spaceId);
  const queryClient = useQueryClient();
  if (!agent?.enableVoice || !conversationId) return null;

  const unavailable = availability === 'unavailable';
  const handleClick = () => {
    if (unavailable) {
      voiceSessionStore.showError('VOICE_CREDENTIAL_MISSING');
      return;
    }
    // Synchronous, pre-await: Safari only honors resume() inside the gesture.
    unlockAudioPlayback();
    void voiceSessionStore.start({
      conversationId,
      agentId: agent.agentId,
      spaceId: agent.spaceId,
      onSessionEnded: ({ conversationId: cid }) => {
        // The session's spoken turns were persisted server-side as normal
        // messages — refetch anything keyed on this conversation so the
        // thread shows them.
        void queryClient.invalidateQueries({
          predicate: (q) => q.queryKey.includes(cid),
        });
      },
    });
  };

  const button = (
    <button
      type="button"
      aria-label="Start voice session"
      title={unavailable ? undefined : 'Start voice session'}
      onClick={handleClick}
      className={cn(
        'mb-[15px] size-9 shrink-0 rounded-full',
        'inline-flex items-center justify-center',
        unavailable
          ? 'text-muted-foreground/40 hover:bg-foreground/5'
          : 'text-muted-foreground hover:bg-foreground/5 hover:text-foreground',
      )}
    >
      <Mic className="h-5 w-5" />
    </button>
  );

  return (
    <ThreadPrimitive.If running={false}>
      {unavailable ? (
        <Tooltip>
          <TooltipTrigger asChild>{button}</TooltipTrigger>
          <TooltipContent side="top">No xAI key connected</TooltipContent>
        </Tooltip>
      ) : (
        button
      )}
    </ThreadPrimitive.If>
  );
}

function ComposerAction() {
  // Tailwind utilities here override the base `.aui-composer-send` styling
  // (size-8, rounded-md, etc.). Final shape: a 36px circular filled button
  // with foreground/background color reversal — primary-action contrast,
  // matching the ChatGPT-style composer reference. `inline-flex` + center
  // alignment guarantees the icon sits dead-center in the circle.
  const sendClass = cn(
    'aui-composer-send shrink-0',
    // `mb-[14px]` sits the button 14px above the pill's bottom border — 2px
    // tighter than the wrapper's `!py-4` (16px), which visually centers the
    // 36px circle against the text's last line (text descenders extend below
    // the baseline, so matching the padding exactly reads as slightly high).
    // With `items-end` on the root, top margin doesn't affect placement.
    'mb-[14px] size-9 rounded-full',
    'inline-flex items-center justify-center',
    // `text-primary` matches the active-nav-item blue (`aria-[current=page]:text-primary`)
    // so the send arrow reads as the same primary-action accent across the app.
    // Dark `bg-background` puts a near-black disc behind the icon — the blue
    // pops where it'd just blend in over the lighter `bg-muted` of the pill.
    //
    // Border switches between blue (enabled, primary ring) and gray (disabled)
    // so the only thing signalling state is the ring colour. We deliberately
    // do NOT use `disabled:opacity-40` here — that fades the entire button
    // including the icon. Instead the `text-primary` icon stays at full
    // brightness in both states; the disabled cue is the dimmer border.
    'bg-background text-primary shadow shadow-black/30',
    'border-2 border-primary/70 disabled:border-foreground/25',
    'hover:bg-background/80',
  );
  return (
    <>
      <ThreadPrimitive.If running={false}>
        <ComposerPrimitive.Send className={sendClass} aria-label="Send">
          <SendHorizontal className="h-5 w-5 translate-x-px" />
        </ComposerPrimitive.Send>
      </ThreadPrimitive.If>
      <ThreadPrimitive.If running>
        <ComposerPrimitive.Cancel className={sendClass} aria-label="Cancel">
          <CircleStop className="h-5 w-5" />
        </ComposerPrimitive.Cancel>
      </ThreadPrimitive.If>
    </>
  );
}
